import { ActionIcon, AppShell, Box, Burger, Group, Menu, Tooltip } from '@mantine/core';
import { IconHelp, IconLogout, IconUserCircle } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import { PRODUCT_NAME } from '../../../brand';
import { useEntity } from '../../../contexts/EntityContext';
import { id } from '../../../utils/entityUtils';
import { NotificationBell } from '../../NotificationBell';
import { CLEAR_OF_AGENT_PANEL } from '../../agent/agentPanel';
import { EntitySelector } from '../EntitySelector';

type LayoutHeaderProps = {
  mobileOpened: boolean;
  toggleMobile: () => void;
  closeMobile: () => void;
};

export function LayoutHeader({ mobileOpened, toggleMobile, closeMobile }: LayoutHeaderProps) {
  const navigate = useNavigate();
  const { selectedEntity, setSelectedEntity } = useEntity();

  return (
    <AppShell.Header style={CLEAR_OF_AGENT_PANEL} data-testid="layout-header">
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          paddingLeft: '16px',
          paddingRight: '16px',
        }}
      >
        {/* Left half does not grow on a phone: at 390px the two halves split the
            row evenly and squeeze the entity selector to 48px, with the user
            menu pushed off the right edge. */}
        <Box
          style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
          flex={{ base: '0 0 auto', sm: 1 }}
        >
          <Burger
            opened={mobileOpened}
            onClick={toggleMobile}
            hiddenFrom="sm"
            size="sm"
            aria-label="Toggle navigation"
            aria-expanded={mobileOpened}
            aria-controls="app-navbar"
          />
          {/* The brand moves to the top of the drawer on a phone. 175px of logo
              is most of what makes the entity selector unusable at 390px, and
              the selector is the primary control on nearly every route. */}
          <Box
            component="img"
            src="/logos/teoai-logo-dark.png"
            alt={PRODUCT_NAME}
            visibleFrom="sm"
            style={{ height: '40px' }}
          />
        </Box>
        <Group
          align="center"
          justify="flex-end"
          wrap="nowrap"
          gap="md"
          style={{ flex: 1, minWidth: 0 }}
        >
          {/* On a phone the selector takes whatever the icons do not; on a
              desktop it stays content-sized exactly as before. Sizing it from
              the outside keeps EntitySelector's prop surface closed. */}
          <Box flex={{ base: 1, sm: '0 0 auto' }} style={{ minWidth: 0 }}>
            <EntitySelector
              placeholder="Select an entity"
              value={selectedEntity ? id(selectedEntity) : null}
              onChange={(entity) => {
                if (entity) setSelectedEntity(entity);
              }}
              entityTypes={['ownership']}
              clearable={false}
              showId={false}
              data-testid="entity-selector"
            />
          </Box>
          <NotificationBell />

          <Tooltip label="Get Help">
            <ActionIcon
              variant="subtle"
              color="gray"
              component="a"
              href="mailto:support@theteoai.com"
              visibleFrom="sm"
            >
              <IconHelp size="1.2rem" />
            </ActionIcon>
          </Tooltip>
          <Menu position="bottom-end" shadow="md" width={180}>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray" data-testid="user-menu-button">
                <IconUserCircle size="1.2rem" />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconUserCircle size={14} />}
                onClick={() => {
                  closeMobile();
                  void navigate('/account');
                }}
              >
                View Profile
              </Menu.Item>
              {/* Phone-only, because the header's help icon is desktop-only:
                  the icon gives up its slot so the entity selector fits, and
                  support has to stay reachable from somewhere. */}
              <Menu.Item
                hiddenFrom="sm"
                component="a"
                href="mailto:support@theteoai.com"
                leftSection={<IconHelp size={14} />}
              >
                Get Help
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                color="red"
                leftSection={<IconLogout size={14} />}
                onClick={() => navigate('/logout')}
              >
                Logout
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </div>
    </AppShell.Header>
  );
}
